"use client";

import { ILeadDropPopup } from "@/api/admin/leadDropPopup";
import { Input } from "@/components/atoms/Input";
import { PhoneNoInput } from "@/components/atoms/PhoneNumber";
import { PopoverSelect } from "@/components/atoms/PopoverSelect";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import {
  trackLeadAoi,
  trackLeadHq,
  trackLeadformError,
  trackLeadformFillstart,
} from "@/customAnalytics/rudderStackEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { RootState } from "@/store/store";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * Lead Drop Delay Form 5026 — the collector KUK shows on a timer and on exit
 * intent, ported from kuk-campus-portal `components/DelayExitForm/index.tsx`.
 *
 * WHY IT IS A SEPARATE SCREEN AND NOT JUST THE POPUP.
 * v1 runs TWO dialogs. The timer opens this one, which asks for all five fields
 * at once behind its own headline ("Need Help To Find The Right Program?"), and
 * Apply Now then opens the lead drop proper with those values, which verifies
 * the number. A CTA click skips this entirely and opens the lead drop directly.
 * So the auto-opened surface is deliberately NOT the same as the clicked one —
 * which is what this app did before, because the timer dispatched the very same
 * `handleOpenLeaddrop` a CTA does.
 *
 * NO SWITCH OF ITS OWN. Only one popup can be active at a time, so this has no
 * row and no toggle to call its own: the university's existing "Enable Delay
 * Form" / "Enable Exit Form" and its three intervals arm it, and popup 5018 is
 * the design that owns it. Its copy lives on that same row under `delay_form_*`.
 *
 * ONE DIALOG, NOT TWO. `OpenLeadDrop` swaps this in for the popup while the open
 * came from the timer or exit intent. Apply Now re-opens the same dialog as a
 * plain REGISTER open carrying the details, which clears that flag, so the lead
 * drop takes over in place and auto-continues on the prefilled phone. The
 * handoff is v1's; only the number of dialogs differs.
 */

interface DelayFormValues {
  phone: string;
  name: string;
  email: string;
  qualification: string;
  course: string;
}

interface FormErrors {
  phone?: string;
  name?: string;
  email?: string;
  qualification?: string;
  course?: string;
}

const FORM_NAME = "lead_drop_delay_form_5026";

/** v1's rules and copy verbatim, including the two that read oddly. */
const PHONE_RE = /^[6-9]\d{9}$/;
const NAME_RE = /^[a-zA-Z\s]+$/;
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * v1 tightens the field spacing on a short window (`window.innerHeight < 700 ?
 * "gap-3" : "gap-10"`) — 40px between five fields does not fit a laptop once the
 * browser chrome is counted. Read after mount, never in the initial state: there
 * is no `window` on the server, and a first render that disagrees with the
 * client's is thrown away.
 */
const SHORT_WINDOW_MAX_HEIGHT = 700;

interface LeaddropDelay5026Props {
  formData?: ILeadDropPopup | null;
  courses?: unknown[];
  qualification?: unknown[];
  onClose?: () => void;
}

const LeaddropDelay5026 = ({
  formData,
  courses,
  qualification,
  onClose,
}: LeaddropDelay5026Props) => {
  const dispatch = useDispatch();
  const prefilledData = useSelector(
    (state: RootState) => state.leaddrop.defaultValues,
  );

  /* Read off the 5018 popup row — the same row the lead drop uses, because only
     one popup can be active at a time. Hence the `delay_form_` prefix on every
     key: `form_title`, `button_text` and the rest already belong to 5018. */
  const cms = (formData || {}) as Record<string, string | undefined>;
  const title = cms.delay_form_title || "Need Help To Find The Right Program?";
  const titleColor = cms.delay_form_title_color || "#9F1C33";
  const subtitle =
    cms.delay_form_subtitle ||
    "Drop your details and our seasoned counselor will get back to you shortly";
  const subtitleColor = cms.delay_form_subtitle_color || "#031F44";
  const buttonText = cms.delay_form_button_text || "Apply Now";
  const buttonColor = cms.delay_form_button_color || "#9F1C33";
  const buttonTextColor = cms.delay_form_button_text_color || "#FFFFFF";
  const markerColor = cms.delay_form_required_marker_color || "#9F1C33";
  const labelColor = cms.delay_form_label_color || "#031F44";
  const errorColor = cms.validation_color || "#FF0000";

  const nameLabel = cms.delay_form_name_label || "Enter Name";
  const phoneLabel = cms.delay_form_phone_label || "Phone Number";
  const emailLabel = cms.delay_form_email_label || "Email ID";
  const qualificationLabel =
    cms.delay_form_qualification_label || "Highest Qualification";
  const courseLabel = cms.delay_form_course_label || "Select Course";

  /* Placeholders are shared with the popup: the same field asked twice should
     not read differently on the two screens. */
  const namePlaceholder = cms.name_placeholder || "Enter Name";
  const phonePlaceholder = cms.phone_placeholder || "Enter your phone number";
  const emailPlaceholder = cms.email_placeholder || "Enter your Email ID";
  const qualificationPlaceholder =
    cms.qualification_placeholder || "Highest Qualification *";
  const coursePlaceholder = cms.course_placeholder || "Area Of Interest";

  const [shortWindow, setShortWindow] = useState(false);
  useEffect(() => {
    const measure = () =>
      setShortWindow(window.innerHeight < SHORT_WINDOW_MAX_HEIGHT);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const [values, setValues] = useState<DelayFormValues>({
    phone: "",
    name: "",
    email: "",
    qualification: "",
    course: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  /* Anything a CTA or an earlier open already collected. v1 restores these from
     sessionStorage; this app keeps the same values on the slice, so they are
     read from there and the storage key stays owned by one place. */
  useEffect(() => {
    if (!prefilledData) return;
    const { phone, name, email, qualification: q, course, interest } =
      prefilledData;
    setValues((prev) => ({
      ...prev,
      ...(phone && { phone }),
      ...(name && { name }),
      ...(email && { email }),
      ...(q && { qualification: q }),
      ...((course || interest) && { course: course || interest || "" }),
    }));
  }, [prefilledData]);

  /**
   * v1 validates on submit only, and SHORT-CIRCUITS: the first failing rule
   * returns, so exactly one message is ever on screen. Reproduced, messages
   * included — "Please enter valid details." really is what it says for a bad
   * name AND for a malformed email.
   */
  const isFormValid = (): boolean => {
    const fail = (field: keyof FormErrors, message: string) => {
      setErrors({ [field]: message });
      trackLeadformError({
        form_name: FORM_NAME,
        field_name: field === "phone" ? "mobile" : field,
        error_message: message,
      });
      return false;
    };

    if (!values.phone.match(PHONE_RE))
      return fail("phone", "Please enter a valid 10-digit phone number");
    if (!values.name.match(NAME_RE))
      return fail("name", "Please enter valid details.");
    if (!values.email) return fail("email", "Please enter valid email address.");
    if (!values.qualification)
      return fail("qualification", "Please select a valid option.");
    if (!values.course) return fail("course", "Please select a course");
    if (!EMAIL_RE.test(values.email.trim()))
      return fail("email", "Please enter valid details.");
    return true;
  };

  /**
   * v1's handoff: close this, open the lead drop with the values. The REGISTER
   * open clears `isDelayFormOpen`, so the dialog swaps to popup 5018, which sees
   * a complete prefill and continues to the OTP on its own.
   */
  const handleApplyNow = () => {
    if (!isFormValid()) return;
    setErrors({});
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
        phoneNumber: values.phone,
        name: values.name,
        email: values.email,
        qualification: values.qualification,
        course: values.course,
      }),
    );
  };

  const fieldClass = (hasError?: string) =>
    cn(
      "mt-1 flex h-9 w-full rounded-md border border-[#BEBEBE] bg-transparent px-3 py-1 text-sm shadow-sm",
      "placeholder:text-[#BEBEBE] focus-visible:outline-none focus-visible:ring-1",
      hasError && "ring-1 ring-red-600",
    );

  const errorLine = (message?: string) =>
    message ? (
      <p className="mt-1 text-xs" style={{ color: errorColor }}>
        {message}
      </p>
    ) : null;

  return (
    <div className="font-poppins relative overflow-hidden rounded-md bg-white">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-2.5 top-2.5 z-30 flex size-6 cursor-pointer items-center justify-center"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.35)" />
          <path
            d="M8 8l8 8M16 8l-8 8"
            stroke="#fff"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <div className="relative z-[1] flex flex-col gap-4 px-5 pb-6 pt-4">
        <div className="flex w-full flex-col items-center gap-5 text-center">
          <h2
            className="text-sm font-bold tab:text-base"
            style={{ color: titleColor }}
          >
            {title}
            {/* v1 hides the second line below its `md`; the headline alone is
                what fits a phone above five fields. */}
            {subtitle ? (
              <span
                className="hidden font-normal tab:block"
                style={{ color: subtitleColor }}
              >
                <br />
                {subtitle}
              </span>
            ) : null}
          </h2>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={cn(
              "flex w-full flex-col text-left",
              shortWindow ? "gap-3" : "gap-10",
            )}
          >
            <div>
              <label
                htmlFor="delay-name"
                className="text-sm font-medium leading-none"
                style={{ color: labelColor }}
              >
                {nameLabel}
                <span style={{ color: markerColor }}>*</span>
              </label>
              <Input
                id="delay-name"
                type="text"
                placeholder={namePlaceholder}
                value={values.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValues((prev) => ({ ...prev, name: e.target.value }))
                }
                onFocus={() =>
                  trackLeadformFillstart({
                    form_name: FORM_NAME,
                    field_name: "name",
                  })
                }
                className={fieldClass(errors.name)}
              />
              {errorLine(errors.name)}
            </div>

            <div>
              <label
                htmlFor="delay-contact"
                className="text-sm font-medium leading-none"
                style={{ color: labelColor }}
              >
                {phoneLabel}
                <span style={{ color: markerColor }}>*</span>
              </label>
              <PhoneNoInput
                /* v1's delay form is a plain <Input type="tel"> — no flag, no
                   "+91", the same as its hero card. */
                hideCountryPrefix
                /* The atom already strips non-digits and caps at 10, so this
                   only has to store what it hands back. */
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValues((prev) => ({ ...prev, phone: e.target.value }))
                }
                value={values.phone}
                onFocus={() =>
                  trackLeadformFillstart({
                    form_name: FORM_NAME,
                    field_name: "mobile",
                  })
                }
                placeholder={phonePlaceholder}
                className={cn(fieldClass(errors.phone), "items-center")}
                inputClassName="border-0 bg-transparent px-0 text-sm focus:border-0 focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
              />
              {errorLine(errors.phone)}
            </div>

            <div>
              <label
                htmlFor="delay-email"
                className="text-sm font-medium leading-none"
                style={{ color: labelColor }}
              >
                {emailLabel}
                <span style={{ color: markerColor }}>*</span>
              </label>
              <Input
                id="delay-email"
                type="text"
                placeholder={emailPlaceholder}
                value={values.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValues((prev) => ({ ...prev, email: e.target.value }))
                }
                onFocus={() =>
                  trackLeadformFillstart({
                    form_name: FORM_NAME,
                    field_name: "email",
                  })
                }
                className={fieldClass(errors.email)}
              />
              {errorLine(errors.email)}
            </div>

            <div>
              <label
                className="text-sm font-medium leading-none"
                style={{ color: labelColor }}
              >
                {qualificationLabel}
                <span style={{ color: markerColor }}>*</span>
              </label>
              <PopoverSelect
                value={values.qualification}
                onValueChange={(v: string) => {
                  setValues((prev) => ({ ...prev, qualification: v }));
                  if (v)
                    trackLeadHq({
                      form_name: FORM_NAME,
                      selected_qualification: v,
                    });
                }}
                onOpenChange={(o: boolean) => {
                  if (o)
                    trackLeadformFillstart({
                      form_name: FORM_NAME,
                      field_name: "qualification",
                    });
                }}
                disabled={!qualification || qualification.length === 0}
                placeholder={qualificationPlaceholder}
                ariaLabel={qualificationLabel}
                options={((qualification || []) as Record<string, string>[]).map(
                  (option) => ({
                    value: option.value || option.id || String(option.id),
                    label: option.name,
                  }),
                )}
                triggerClassName={cn(fieldClass(errors.qualification), "py-2", {
                  "cursor-not-allowed opacity-50":
                    !qualification || qualification.length === 0,
                })}
              />
              {errorLine(errors.qualification)}
            </div>

            <div>
              <label
                className="text-sm font-medium leading-none"
                style={{ color: labelColor }}
              >
                {courseLabel}
                <span style={{ color: markerColor }}>*</span>
              </label>
              <PopoverSelect
                value={values.course}
                onValueChange={(v: string) => {
                  setValues((prev) => ({ ...prev, course: v }));
                  if (v)
                    trackLeadAoi({ form_name: FORM_NAME, selected_program: v });
                }}
                onOpenChange={(o: boolean) => {
                  if (o)
                    trackLeadformFillstart({
                      form_name: FORM_NAME,
                      field_name: "course",
                    });
                }}
                disabled={!courses || courses.length === 0}
                placeholder={coursePlaceholder}
                ariaLabel={courseLabel}
                heading="Interest *"
                options={((courses || []) as Record<string, string>[]).map(
                  (option) => ({
                    value: option.erp_unicode || option.program_name,
                    label: option.program_name,
                  }),
                )}
                triggerClassName={cn(fieldClass(errors.course), "py-2", {
                  "cursor-not-allowed opacity-50":
                    !courses || courses.length === 0,
                })}
              />
              {errorLine(errors.course)}
            </div>

            <div>
              <button
                type="button"
                onClick={handleApplyNow}
                className="h-[40px] w-full rounded-md px-5 py-2 text-xs font-medium shadow transition-opacity"
                style={{ background: buttonColor, color: buttonTextColor }}
              >
                {buttonText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeaddropDelay5026;
